import { Contract, Page } from 'types';
import { MigrationError } from 'types/migration';
import fetchWithRetry from './fetch-retry';

export const fetchContractsList = async (params: {
  customerIds?: number[];
  pageNum?: number;
  pageSize?: number;
  from?: Date;
  to?: Date;
  sortField?: string;
  sortDirection?: string;
  filterString?: string;
  locationId?: number;
}): Promise<Page<Contract> | null> => {
  const url = new URL('/api/contracts', window.location.origin);
  // Add query params
  params.customerIds?.forEach(customerId => url.searchParams.append('customerIds', `${customerId}`));
  url.searchParams.append('pageNum', `${params.pageNum ?? 0}`);
  url.searchParams.append('pageSize', `${params.pageSize ?? 100}`);

  if (params.sortField) {
    url.searchParams.append('sortField', params.sortField);
  }
  if (params.sortDirection) {
    url.searchParams.append('sortDirection', params.sortDirection);
  }
  if (params.filterString) {
    url.searchParams.append('filterString', params.filterString);
  }
  if (params.locationId) {
    url.searchParams.append('locationId', `${params.locationId}`);
  }

  return await fetchWithRetry<Page<Contract> | null>(url, { method: 'GET' }, null);
};

export type ContractMigrationMode = 'UPSERT' | 'REPLACE';

export interface BulkContractMigrationError {
  rowNumber?: number;
  locationCode?: string;
  accountCode?: string;
  message?: string;
  rawData?: string;
}

export interface BulkLocationSummary {
  locationId: number;
  locationCode: string;
  recordsRead: number;
  recordsImported: number;
  recordsFailed: number;
}

export interface BulkContractMigrationResponse {
  mode: ContractMigrationMode;
  recordsRead: number;
  recordsImported: number;
  recordsFailed: number;
  errors: BulkContractMigrationError[];
  locations: BulkLocationSummary[];
}

export interface ContractMigrationResponse {
  mode: ContractMigrationMode | 'APPEND';
  recordsRead: number;
  recordsImported: number;
  recordsFailed: number;
  errors: MigrationError[];
}

/**
 * Uploads a contract workbook for a location in UPSERT or REPLACE mode. Validation failures come
 * back as a structured 4xx with the same shape as a success summary, so the response is parsed
 * (and returned) regardless of status; a non-JSON transport failure throws.
 */
export const uploadContractMigration = async (
  locationId: number,
  mode: ContractMigrationMode,
  file: File
): Promise<ContractMigrationResponse> => {
  const token = localStorage.getItem('token');
  const formData = new FormData();
  formData.append('file', file);
  const url = new URL('/api/contracts/migration', window.location.origin);
  url.searchParams.append('locationId', String(locationId));
  url.searchParams.append('mode', mode);
  const res = await fetch(url.toString(), {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData
  });
  const body = (await res.json().catch(() => null)) as ContractMigrationResponse | null;
  if (!res.ok && !body) {
    throw new Error(`Contract migration failed (HTTP ${res.status})`);
  }
  return body ?? { mode, recordsRead: 0, recordsImported: 0, recordsFailed: 1, errors: [] };
};

export const downloadContractMigrationTemplate = (): void => {
  const token = localStorage.getItem('token');
  fetch('/api/contracts/migration/template', { headers: { Authorization: `Bearer ${token}` } })
    .then(res => res.blob())
    .then(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'contracts-migration-template.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    });
};

export const uploadBulkContractMigration = async (
  mode: ContractMigrationMode,
  file: File
): Promise<BulkContractMigrationResponse> => {
  const token = localStorage.getItem('token');
  const formData = new FormData();
  formData.append('file', file);
  const url = new URL('/api/contracts/migration/bulk', window.location.origin);
  url.searchParams.append('mode', mode);
  const res = await fetch(url.toString(), {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData
  });
  const body = (await res.json().catch(() => null)) as BulkContractMigrationResponse | null;
  if (!res.ok && !body) {
    throw new Error(`Bulk contract migration failed (HTTP ${res.status})`);
  }
  return (
    body ?? {
      mode,
      recordsRead: 0,
      recordsImported: 0,
      recordsFailed: 1,
      errors: [],
      locations: []
    }
  );
};

export const downloadBulkContractMigrationTemplate = (): void => {
  const token = localStorage.getItem('token');
  fetch('/api/contracts/migration/bulk/template', {
    headers: { Authorization: `Bearer ${token}` }
  })
    .then(res => res.blob())
    .then(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'contracts-bulk-migration-template.csv';
      a.click();
      URL.revokeObjectURL(url);
    });
};

export const getContract = async (id: number): Promise<Contract | null> => {
  const url = new URL(`/api/contracts/${id}`, window.location.origin);
  return await fetchWithRetry<Contract | null>(url, { method: 'GET' }, null);
};

export const getContractForSizeType = async (params: {
  sizeType: string;
  customerId: number;
  date: Date;
  endDate?: Date;
  locationId?: number;
}): Promise<Contract | null> => {
  const url = new URL(`/api/contracts/sizeType/${params.sizeType}`, window.location.origin);
  url.searchParams.append('customerId', `${params.customerId}`);
  url.searchParams.append('date', params.date.toISOString());
  url.searchParams.append('locationId', `${params.locationId}`);

  return fetchWithRetry<Contract | null>(url, { method: 'GET' }, null);
};
